import React from "react";
import { AlertTriangle, RotateCcw, Home, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Global Error Boundary — evita que um erro de render em qualquer página
 * derrube toda a aplicação para uma tela branca. Mostra um fallback amigável
 * com opções de recuperação (tentar novamente, recarregar, voltar ao início).
 *
 * Error boundaries precisam ser class components (getDerivedStateFromError /
 * componentDidCatch).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log para o console (útil para suporte / futura telemetria).
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Render error capturado:", error, info?.componentStack);
    this.setState({ info });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, info: null, showDetails: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, info, showDetails } = this.state;
    const detail = (error?.stack || String(error || "")) + (info?.componentStack || "");

    return (
      <div
        className="min-h-screen w-full bg-white flex items-center justify-center px-6 py-16"
        data-testid="error-boundary-fallback"
      >
        <div className="w-full max-w-lg">
          <div className="h-12 w-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>

          <h1 className="mt-6 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900">
            Algo deu errado nesta tela
          </h1>
          <p className="mt-3 text-base text-zinc-500 leading-relaxed">
            Tivemos um problema inesperado ao carregar esta página. Seus dados estão
            seguros. Tente novamente ou recarregue — se persistir, fale com o suporte.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={this.handleRetry}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-11 font-semibold"
              data-testid="error-boundary-retry-button"
            >
              <RotateCcw className="h-4 w-4 mr-2" /> Tentar novamente
            </Button>
            <Button
              onClick={this.handleReload}
              variant="outline"
              className="h-11 font-semibold"
              data-testid="error-boundary-reload-button"
            >
              Recarregar página
            </Button>
            <Button
              onClick={this.handleHome}
              variant="ghost"
              className="h-11 font-semibold text-zinc-600"
              data-testid="error-boundary-home-button"
            >
              <Home className="h-4 w-4 mr-2" /> Início
            </Button>
          </div>

          {detail && (
            <div className="mt-8">
              <button
                type="button"
                onClick={() => this.setState({ showDetails: !showDetails })}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-600 transition-colors"
                data-testid="error-boundary-details-toggle"
              >
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${showDetails ? "rotate-180" : ""}`}
                />
                Detalhes técnicos
              </button>
              {showDetails && (
                <pre
                  className="mt-3 max-h-56 overflow-auto rounded-md border border-zinc-200 bg-zinc-50 p-3 text-[11px] leading-relaxed text-zinc-600 whitespace-pre-wrap break-words"
                  data-testid="error-boundary-details"
                >
                  {detail}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
